/**
 * 认证状态钩子
 * 管理用户认证状态和权限
 */

import { useState, useEffect, useCallback } from 'react';
import { authService, UserInfo, AuthState } from '../services/authService';

/**
 * 认证状态钩子
 */
export function useAuth() {
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    user: null,
    token: null,
    refreshToken: null,
    permissions: [],
  });

  const [loading, setLoading] = useState(true);

  // 初始化认证状态
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        setLoading(true);
        
        // 检查本地存储的认证信息
        const token = localStorage.getItem('admin_token');
        const userInfo = localStorage.getItem('user_info');

        if (token && userInfo) {
          // 直接设置认证状态
          setAuthState(authService.getAuthState());
        } else {
          // 如果没有token，确保状态是未认证
          setAuthState({
            isAuthenticated: false,
            user: null,
            token: null,
            refreshToken: null,
            permissions: [],
          });
        }
      } catch (error) {
        console.error('初始化认证状态失败:', error);
        setAuthState({
          isAuthenticated: false,
          user: null,
          token: null,
          refreshToken: null,
          permissions: [],
        });
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();

    // 监听认证状态变化事件
    const handleAuthStateChange = (event: CustomEvent) => {
      setAuthState(event.detail);
    };

    window.addEventListener('authStateChange', handleAuthStateChange as EventListener);

    // 监听localStorage变化（作为备用机制）
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'admin_token' || e.key === 'user_info') {
        const token = localStorage.getItem('admin_token');
        const userInfo = localStorage.getItem('user_info');
        
        if (token && userInfo) {
          setAuthState(authService.getAuthState());
        } else {
          setAuthState({
            isAuthenticated: false,
            user: null,
            token: null,
            refreshToken: null,
            permissions: [],
          });
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('authStateChange', handleAuthStateChange as EventListener);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // 登录
  const login = useCallback(async (username: string, password: string) => {
    try {
      setLoading(true);
      const response = await authService.login({ username, password });
      
      if (response.code === 200) {
        setAuthState(authService.getAuthState());
        return { success: true, data: response.data };
      } else {
        throw new Error(response.message || '登录失败');
      }
    } catch (error: any) {
      throw error; // 重新抛出错误，让LoginPage能够捕获
    } finally {
      setLoading(false);
    }
  }, []);

  // 登出
  const logout = useCallback(async () => {
    try {
      await authService.logout();
      // authService.logout() 已经会触发 authStateChange 事件和清除 localStorage
      // 这里再确保状态更新
      setAuthState({
        isAuthenticated: false,
        user: null,
        token: null,
        refreshToken: null,
        permissions: [],
      });
    } catch (error) {
      console.error('登出失败:', error);
      // 即使登出请求失败，也清除本地状态
      setAuthState({
        isAuthenticated: false,
        user: null,
        token: null,
        refreshToken: null,
        permissions: [],
      });
    }
  }, []);

  // 更新用户信息
  const updateUserInfo = useCallback((userInfo: Partial<UserInfo>) => {
    // 简化实现，直接更新本地存储
    const currentUser = authState.user;
    if (currentUser) {
      const updatedUser = { ...currentUser, ...userInfo };
      localStorage.setItem('user_info', JSON.stringify(updatedUser));
      setAuthState({ ...authState, user: updatedUser });
    }
  }, [authState]);

  // 检查权限
  const hasPermission = useCallback((permission: string) => {
    return authState.permissions.includes(permission);
  }, [authState.permissions]);

  // 检查角色
  const hasRole = useCallback((roleKey: string) => {
    return authState.user?.roles.some(role => role.code === roleKey) || false;
  }, [authState.user]);

  // 获取用户信息
  const getUserInfo = useCallback(async () => {
    try {
      const userInfo = await authService.getUserInfo();
      setAuthState(authService.getAuthState());
      return userInfo;
    } catch (error) {
      console.error('获取用户信息失败:', error);
    }
    return null;
  }, []);

  return {
    // 状态
    isAuthenticated: authState.isAuthenticated,
    user: authState.user,
    token: authState.token,
    loading,
    
    // 方法
    login,
    logout,
    updateUserInfo,
    hasPermission,
    hasRole,
    getUserInfo,
  };
}

/**
 * 权限检查钩子
 */
export function usePermission(permission: string) {
  const { hasPermission } = useAuth();
  return hasPermission(permission);
}

/**
 * 角色检查钩子
 */
export function useRole(roleKey: string) {
  const { hasRole } = useAuth();
  return hasRole(roleKey);
}

